import React, { useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { UserIdentityContext } from '../contexts/user_identity_context';
import { RootStoreContext } from '../stores/root_store';

import Button from '@mui/joy/Button';
// import FormControl from '@mui/joy/FormControl';
// import FormLabel from '@mui/joy/FormLabel';
// import FormHelperText from '@mui/joy/FormHelperText';
import Textarea from '@mui/joy/Textarea';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

import Grid from '@mui/joy/Grid';
import Stack from '@mui/joy/Stack';


import useCollabInvite from '../hooks/useCollabInvite';
import useDedicatedAssignment from '../hooks/useDedicatedAssignment';


import { ProjectProps } from 'stores/project_store';

import CollabCreateCopy from 'components/CollabCreateCopy';
import { Success, Error } from 'components/FancyToast';
import { forageGet, forageSet } from 'utils/misc';
import { FHRequestType } from 'stores/fhrequest_store';

import useCollabs from 'hooks/useCollabs';
import useFHRequest from 'hooks/useFHRequest';
import { CollabProps } from 'stores/collab_store';
// import { IdentitySelectorForm } from './IdentitySelector';
// import CadenceForm from './CadenceForm';

import CollabCreateInviteSelector from './CollabCreateInviteSelector';
type CollabFrameProps = {
    project?: ProjectProps;
    defaultInitialInvite?: 'day-ones' | 'dedicated' | 'standard-service' | undefined | string;
    // onClickNew: () => void;
    // onClickEdit: () => void;
};


const FORAGE_INVITE_KEY = 'initial-invite-[KEY]';

export const CollabCreateForm = observer(({ project, defaultInitialInvite=undefined }: CollabFrameProps) => {
    const { currentIdentity } = useContext(UserIdentityContext);
    const { createRequest } = useFHRequest();
    const { createCollab } = useCollabs();

    const [title, _] = React.useState('');
    const [description, setDescription] = React.useState('');
    const rootStore = useContext(RootStoreContext);
    const projectKey = project?.key;
    const { inviteDedicated } = useCollabInvite();

    const [selectedCadence] = React.useState(project?.cadence || 'chat');
    const { hasDedicatedAssistant, fetchDedicatedAssignmentsByIdentity } = useDedicatedAssignment();

    // const weHaveDedicated = hasDedicatedAssistant(savedIdentity?.key);
    const [weSetDedicated, setWeSetDedicated] = React.useState(false);
    const [initialInvite, setInitialInvite] = React.useState(defaultInitialInvite || 'initial');
    const [loadedForagePref, setLoadedForagePref] = React.useState(false);
    const [creatingCollab, setCreatingCollab] = React.useState(false);
    //    const [hideDedicated, setHideDedicated] = React.useState(defaultInitialInvite === "standard-service");

    const hideDedicated = defaultInitialInvite === "standard-service" || !hasDedicatedAssistant(currentIdentity.key);
    console.log("hideDedicated: %o", hideDedicated);

    const navigate = useNavigate();

    // const hideDedicated = !hasDedicatedAssistant(currentIdentity.key) || defaultInitialInvite === "standard-service";
    const setFromForage = useCallback(() => {
        if(defaultInitialInvite !== undefined) {
            setInitialInvite(defaultInitialInvite);
            return;
        }
        const key = FORAGE_INVITE_KEY.replace('[KEY]', currentIdentity.key);
        forageGet(key).then((val) => {
            if (typeof val == 'string') {
                setInitialInvite(val);
                setLoadedForagePref(true);
            }
        })
    }, [currentIdentity.key, setInitialInvite, setLoadedForagePref]);


    // should we hide the dedicated inviter?
    useEffect(() => {
        if (!currentIdentity?.uuid) { return; }

        fetchDedicatedAssignmentsByIdentity(currentIdentity.uuid, currentIdentity.uuid)
            .then(() => {
                // setHideDedicated(!hasDedicatedAssistant(currentIdentity.key));
                console.log("fetched dedicated assignments, ", !hasDedicatedAssistant(currentIdentity.key));
            });
    }, [currentIdentity?.uuid, fetchDedicatedAssignmentsByIdentity]);

    useEffect(() => {
        if (!currentIdentity?.key) { return; }
        if (defaultInitialInvite == "standard-service") return;

        // Already loaded and set
        if (loadedForagePref) {
            return;
        }

        const weHaveDedicated = hasDedicatedAssistant(currentIdentity.key);

        if (weHaveDedicated) {
            if (!loadedForagePref && !weSetDedicated) {
                if (initialInvite !== 'initial') {
                    console.log("setting to dedicated")
                    setInitialInvite('dedicated');
                    setWeSetDedicated(true);
                }
                else {
                    console.log("already was dedicated");
                }
            } else {
                console.log("not overwriting dedicated existing setting");
            }
        }
    }, [currentIdentity?.key,
        hasDedicatedAssistant,
        initialInvite,
        loadedForagePref,
        weSetDedicated,
        setWeSetDedicated,
        setInitialInvite,
    defaultInitialInvite]);


    useEffect(() => {
        setFromForage();
    }, [setFromForage]);

    function updateInitialInvite(val: string) {
        if (currentIdentity.key) {
            const key = FORAGE_INVITE_KEY.replace('[KEY]', currentIdentity.key);
            forageSet(key, val);
            setInitialInvite(val);
            console.log("dedicated setting initial invite to: %s", val);
        }
        else {
            console.error("No current identity, can't set initial invite");
        }
    }


    function redirectToCollab(collabKey: string) {
        if(projectKey)
            navigate(`/project/${projectKey}/collab/${collabKey}`);
        else
            navigate(`/collab/${collabKey}`);
    }


    async function inviteCollaborator(inviteType: string, collabKey: string) {
        if (inviteType === 'day-ones') {
            return rootStore.collabInviteStore.create(collabKey, currentIdentity.uuid).
                then(() => {
                    Success("We're looking for someone now!");
                });
        }
        else if (inviteType === 'dedicated') {
            return inviteDedicated(collabKey, currentIdentity.uuid).
                then(() => {
                    Success("We're reaching out to your dedicated now!");
                });
        }
        return;
    }

    function tryCollabCreate() {
        if (currentIdentity.uuid) {
            console.log("creating collab: %o", initialInvite);
            if (initialInvite == "standard-service") {
                createRequest(currentIdentity.uuid, title, description, project?.uuid)
                    .then((resp: FHRequestType | null) => {
                        if (!resp || !resp.key) {
                            Error("There was an issue and we couldn't create request!");
                            return;
                        }
                        Success("Created Request!");
                        navigate(`/request/${resp.key}`);
                    }).catch((err) => {
                        console.error("error creating request: %o", err);
                        Error("There was an issue and we couldn't create request!");
                    });
                return;
            }

            setCreatingCollab(true);
            const collab = {
                title: title,
                description: description,
                cadence: selectedCadence,
                project: projectKey,
                identity: currentIdentity.uuid
            } as CollabProps;
            createCollab(collab)
                .then(async (resp) => {
                    setCreatingCollab(false);
                    // console.log("created collab: resp: %o initialInvite: %s", resp, initialInvite);
                    const collabKey = resp.key
                    if (!collabKey) {
                        Error("There was an issue and we couldn't create collab!");
                        return;
                    }

                    inviteCollaborator(initialInvite, collabKey)
                        .catch((err) => {
                            if (err.message.startsWith("not enough credit:")) {
                                Error("You don't have enough credits to invite someone. Saved as 'Just Me' for now.");
                            }
                            else {
                                Error("There was an issue inviting a collaborator!");
                                console.error("error inviting collaborator: %o", err);
                            }
                        }).
                        finally(() => {
                            setCreatingCollab(false);
                            redirectToCollab(collabKey);
                        });

                }).
                catch((err) => {

                        Error("There was an error creating this collab");
                        console.error("error creating collab: %o", err);

                });
        }
    }

    return (
        <Stack
            p={2}
            spacing={3}
        >
            <Typography
                level="h2"
            >What are we getting done?
            </Typography>
            <Stack spacing={2}>

                <Textarea
                    minRows={5}
                    // html input attribute
                    onChange={(e) => { setDescription(e.target.value); }}
                    value={description}
                    name="description"
                    placeholder="&quot;Call enough stores to get a sense for the current price of bananas in Tulsa&quot;"
                />
            </Stack>

            <Typography
                level="h3"
            >Who should work on it?
            </Typography>

            <Stack
                spacing={1}
                pt={2}>
                <CollabCreateInviteSelector
                           hideDayOnes={true}
                           hideJustMe={initialInvite === "standard-service"}
                    hideDedicated={hideDedicated}
                    selectedValue={initialInvite}
                    setValue={updateInitialInvite} />
            </Stack>

            <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
            >
                <Button
                    size="sm"
                    variant="plain"
                    onClick={() => { window.history.back(); }}>Cancel</Button>

                <Box sx={{ flexGrow: 1 }} />
                {creatingCollab ?
                    <Button disabled={true} sx={{ mt: 1 }}>Creating</Button> :
                    <Button onClick={tryCollabCreate} sx={{ mt: 1 }}>Create</Button>}
            </Stack>

        </Stack>
    );
});


const CollabCreate = observer(({ project }: CollabFrameProps) => {
    return (
        <Stack pb={5} >

            <Grid container>
                <Grid xs={12} sm={8} >
                    <CollabCreateForm project={project} />
                </Grid>
                <Grid xs={12} sm={4}
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                    }}
                >
                    <CollabCreateCopy />
                </Grid>
            </Grid>
        </Stack>
    );

});

export default CollabCreate;
